import {
  FileDownload as FileDownloadIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useInView } from 'react-intersection-observer';
import { NoResultAlert } from 'src/components/noResultAlert';
import { Progress } from 'src/components/progress/Progress';
import { CustomTableHead } from 'src/components/table/LegacyTable/LegacyCustomTable.styled';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { MAX_DESCRIPTION_LENGTH } from 'src/constants';
import { type IUserActions } from 'src/interfaces';
import { lang } from 'src/utils';

import { useListUserActionsPage } from '../../hooks/ListUserActionsPage.hook';
import { SORT_DIRECTION } from '../../ListUserActionsPage.interface';
import {
  ArrowIcon,
  ButtonWrapper,
  CustomTableCell,
  CustomTableRow,
  InViewBoxContainer,
  KeyboardArrowDownIconStyled,
  KeyboardArrowUpIconStyled,
  SortIcons,
  TableBodyContainer,
  TableHeadCell,
} from '../../ListUserActionsPage.styled';
import { accordionIconStyles } from './ActionTrackingGridTable.styled';

export const ActionTrackingGridTable = () => {
  const { ref: inViewRef, inView } = useInView({ threshold: 0.1 });
  const tableRef = useRef<HTMLDivElement>(null);
  const {
    isLoading,
    getNextActionTrackingPage,
    columns,
    handleSort,
    activeColumn,
    userActions,
    sorting,
    hasNextPage,
  } = useListUserActionsPage();

  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  const handleExportClick = () => {
    if (
      !userActions ||
      userActions?.pages.flatMap((page) => page.data).length === 0 ||
      !isLoading
    ) {
      return;
    }
  };

  const handleToggleRow = (index: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  useEffect(() => {
    if (inView) {
      getNextActionTrackingPage();
    }
  }, [getNextActionTrackingPage, inView]);

  if (isLoading) {
    return <Progress />;
  }
  const hasData =
    userActions && userActions?.pages.flatMap((page) => page.data).length > 0;

  return (
    <>
      {/* TODO: move this to header as action bar */}
      <ButtonWrapper>
        <CSVLink
          data={userActions?.pages.flatMap((page) => page.data) || []}
          headers={columns}
          filename="Action_Tracking.csv"
          target="_blank"
          style={{ textDecoration: 'none' }}>
          <Button
            style={{ display: 'none' }} // temporary fix for hiding the button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportClick}>
            {lang.export}
          </Button>
        </CSVLink>
      </ButtonWrapper>
      {hasData ? (
        <div ref={tableRef}>
          <TableBodyContainer>
            <Table stickyHeader>
              <CustomTableHead>
                <TableRow>
                  <CustomTableCell />
                  {columns.map((column) => (
                    <CustomTableCell
                      key={column.key}
                      isdoublewidth={column.key}>
                      <TableHeadCell>
                        {column.label}
                        {column.sortIcon && (
                          <SortIcons>
                            <ArrowIcon
                              role="button"
                              aria-label={`Sort ${column.label} ascending`}
                              data-testid={`sort-asc-${column.key}`}
                              onClick={() =>
                                handleSort(column.key, SORT_DIRECTION.ASC)
                              }
                              active={activeColumn === column.key}>
                              <KeyboardArrowUpIconStyled
                                active={
                                  activeColumn === column.key &&
                                  sorting.order === SORT_DIRECTION.ASC
                                }
                              />
                            </ArrowIcon>
                            <ArrowIcon
                              role="button"
                              aria-label={`Sort ${column.label} descending`}
                              data-testid={`sort-desc-${column.key}`}
                              onClick={() =>
                                handleSort(column.key, SORT_DIRECTION.DESC)
                              }
                              active={activeColumn === column.key}>
                              <KeyboardArrowDownIconStyled
                                active={
                                  activeColumn === column.key &&
                                  sorting.order === SORT_DIRECTION.DESC
                                }
                              />
                            </ArrowIcon>
                          </SortIcons>
                        )}
                        {column.label === lang.changesMade && (
                          <CustomTooltip
                            title={lang.changesMadeInfo}
                            skipValidateOverflow={true}>
                            <IconButton disableRipple data-testid="info-icon">
                              <InfoIcon />
                            </IconButton>
                          </CustomTooltip>
                        )}
                      </TableHeadCell>
                    </CustomTableCell>
                  ))}
                </TableRow>
              </CustomTableHead>

              <TableBody>
                {!isLoading &&
                  userActions?.pages.map((page) =>
                    page.data.map((row, rowIndex) => {
                      const isExpanded = expandedRows[rowIndex] || false;

                      return (
                        <CustomTableRow
                          key={row.id}
                          sx={isExpanded && { verticalAlign: 'top' }}>
                          <CustomTableCell>
                            {row.description &&
                              row.description.length >
                                MAX_DESCRIPTION_LENGTH && (
                                <IconButton
                                  onClick={() => handleToggleRow(rowIndex)}
                                  size="small"
                                  aria-label="Expand row">
                                  <ExpandMoreIcon
                                    style={accordionIconStyles(isExpanded)}
                                  />
                                </IconButton>
                              )}
                          </CustomTableCell>

                          {columns.map((column) => {
                            const content = String(
                              row[column.key as keyof IUserActions] ?? '',
                            );

                            return (
                              <CustomTableCell
                                key={column.key}
                                isdoublewidth={column.key}
                                sx={
                                  isExpanded && {
                                    verticalAlign: 'top',
                                    paddingTop: '20px',
                                  }
                                }>
                                {column.key === 'description' && isExpanded ? (
                                  <>
                                    {content
                                      .split('.,')
                                      .map((part, index, parts) => (
                                        <Typography
                                          key={`${index}-${part}`}
                                          width="100%"
                                          variant="body2"
                                          sx={{ overflowWrap: 'anywhere' }}>
                                          {part}
                                          {index < parts.length - 1 && '.'}
                                        </Typography>
                                      ))}
                                  </>
                                ) : (
                                  <CustomTooltip
                                    title={content}
                                    overflowWidth={280}
                                    cursor="pointer"
                                    placement="bottom">
                                    <Typography variant="body2">
                                      {content}
                                    </Typography>
                                  </CustomTooltip>
                                )}
                              </CustomTableCell>
                            );
                          })}
                        </CustomTableRow>
                      );
                    }),
                  )}
              </TableBody>
            </Table>
            {hasNextPage && (
              <InViewBoxContainer>
                <Box ref={inViewRef} />
              </InViewBoxContainer>
            )}
          </TableBodyContainer>
        </div>
      ) : (
        <NoResultAlert />
      )}
    </>
  );
};
